// Weekday helpers for ParkConnect.
//
// Availability is a weekly Monday-to-Friday pattern, not a calendar: a permit
// sits unused on the same weekday every week. Nothing in the UI ever holds a
// Date, which is also why there is no timezone question to get wrong.
//
// Five days, because that is what the board filter offers and what the store
// accepts. `letter` is the course-timetable shorthand — M T W H F — so Thursday
// reads as H rather than a second T.

export const DAYS = [
  { day: 1, short: 'Mon', long: 'Monday', letter: 'M' },
  { day: 2, short: 'Tue', long: 'Tuesday', letter: 'T' },
  { day: 3, short: 'Wed', long: 'Wednesday', letter: 'W' },
  { day: 4, short: 'Thu', long: 'Thursday', letter: 'H' },
  { day: 5, short: 'Fri', long: 'Friday', letter: 'F' },
];

export const dayShort = (day) => DAYS.find(d => d.day === day)?.short || '';
export const dayLong = (day) => DAYS.find(d => d.day === day)?.long || '';
export const dayLetter = (day) => DAYS.find(d => d.day === day)?.letter || '';

export const sortSlots = (slots) => [...(slots || [])].sort((a, b) => a.day - b.day);

/** "9:00 AM" from a 24h "09:00" — 24h is what the inputs and the store use. */
export function timeLabel(hhmm) {
  const [h, min] = String(hhmm).split(':').map(Number);
  if (!Number.isFinite(h)) return '';
  const period = h < 12 ? 'AM' : 'PM';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(min).padStart(2, '0')} ${period}`;
}

export const windowLabel = (slot) =>
  (slot.allDay ? 'all day' : `${timeLabel(slot.start)} – ${timeLabel(slot.end)}`);

/**
 * The whole pattern in as few lines as it takes to read.
 *
 * Days sharing a window are collapsed, and a run of three or more becomes a
 * range: someone scanning the board wants "Mon–Fri, 7:30 AM – 12:00 PM", not
 * five rows saying the same thing. Returns `[{ days, window }]`.
 */
export function summarize(slots) {
  const groups = [];
  for (const slot of sortSlots(slots)) {
    const window = windowLabel(slot);
    const last = groups[groups.length - 1];
    if (last && last.window === window) last.days.push(slot.day);
    else groups.push({ window, days: [slot.day] });
  }
  return groups.map(({ days, window }) => ({
    window,
    days: runsOf(days).map(run => (
      run.length >= 3 ? `${dayShort(run[0])}–${dayShort(run[run.length - 1])}` : run.map(dayShort).join(', ')
    )).join(', '),
  }));
}

/** Split days into consecutive runs. */
function runsOf(days) {
  const runs = [];
  for (const day of days) {
    const last = runs[runs.length - 1];
    if (last && day === last[last.length - 1] + 1) last.push(day);
    else runs.push([day]);
  }
  return runs;
}

/** One line for a compact space: "Mon–Fri 7:30 AM – 12:00 PM". */
export function shortSummary(slots) {
  const parts = summarize(slots);
  if (!parts.length) return 'No days set';
  return parts.map(p => `${p.days} ${p.window}`).join(' · ');
}
